import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { UtensilsCrossed, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';

export function WelcomeModal() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if modal should be shown (once per 24 hours)
    const checkAndShowModal = () => {
      const lastShown = localStorage.getItem('welcomeModalLastShown');
      const now = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

      // Show modal if:
      // 1. Never shown before (lastShown is null)
      // 2. Or 24 hours have passed since last shown
      if (!lastShown || (now - parseInt(lastShown)) >= twentyFourHours) {
        // Show modal after 5 seconds
        const timer = setTimeout(() => {
          setIsOpen(true);
          // Store the current timestamp when modal is shown
          localStorage.setItem('welcomeModalLastShown', now.toString());
        }, 5000);

        return () => clearTimeout(timer);
      }
    };

    const cleanup = checkAndShowModal();
    return cleanup;
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent 
        showCloseButton={true}
        className="sm:max-w-md overflow-hidden"
      >
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            type: "spring", 
            stiffness: 200, 
            damping: 20,
            delay: 0.1 
          }}
          className="flex flex-col items-center gap-6 py-2"
        >
          {/* Icon Container with Animation */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 200, 
              damping: 15,
              delay: 0.2 
            }}
            className="relative"
          >
            <div className="relative flex items-center justify-center">
              {/* Background circle with gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400/20 via-amber-400/20 to-yellow-400/20 rounded-full blur-xl animate-pulse" />
              
              {/* Icon container */}
              <div className="relative bg-gradient-to-br from-orange-500 to-amber-500 p-4 rounded-full shadow-lg">
                <UtensilsCrossed className="w-8 h-8 text-white" />
              </div>
              
              {/* Sparkle decorations */}
              <motion.div
                animate={{ 
                  rotate: [0, 360],
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute -top-1 -right-1"
              >
                <Sparkles className="w-5 h-5 text-amber-400" />
              </motion.div>
              <motion.div
                animate={{ 
                  rotate: [360, 0],
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
                className="absolute -bottom-1 -left-1"
              >
                <Sparkles className="w-4 h-4 text-orange-400" />
              </motion.div>
            </div>
          </motion.div>

          {/* Text Content with Animation */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ 
              delay: 0.3,
              duration: 0.5
            }}
            className="text-center space-y-2"
          >
            <DialogDescription className="text-base sm:text-lg text-foreground font-medium leading-relaxed px-2">
              We specialize in making the most delicious Nigerian Delicacies according to your taste bud here.
            </DialogDescription>
          </motion.div>

          {/* Button with Animation */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ 
              delay: 0.4,
              duration: 0.5
            }}
            className="w-full"
          >
            <DialogFooter className="sm:justify-center">
              <Button 
                onClick={handleClose} 
                className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold px-8 py-2 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Ok
              </Button>
            </DialogFooter>
          </motion.div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}

